import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "../../lib/api";

/**
 * FavoritesActivityPill — social-proof "X campers saved this · last Nm ago".
 *
 * Renders nothing if zero favourites exist (no false-impression of activity).
 * Polls /api/campsites/{id}/favorites-activity once on mount.
 * Re-fetches when `bump` prop changes — caller bumps this when the user
 * toggles their own favourite, so the count reflects the change immediately.
 */
export default function FavoritesActivityPill({ campsiteId, bump = 0, className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/campsites/${campsiteId}/favorites-activity`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [campsiteId, bump]);

  if (!data || !data.total || data.total < 1) return null;

  // Relative timestamp
  let when = null;
  if (data.last_saved_at) {
    try {
      const ms = Date.now() - new Date(data.last_saved_at).getTime();
      const m = Math.max(0, Math.floor(ms / 60000));
      if (m < 1) when = "just now";
      else if (m < 60) when = `${m}m ago`;
      else if (m < 60 * 24) when = `${Math.floor(m / 60)}h ago`;
      else if (m < 60 * 24 * 30) when = `${Math.floor(m / 60 / 24)}d ago`;
      else when = null;
    } catch {}
  }

  const camperWord = data.total === 1 ? "camper" : "campers";
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#fef2f4] border border-[#ffc7d1] text-[#c8194c] mono text-[10px] sm:text-[11px] uppercase tracking-wider font-bold ${className}`}
      data-testid="favorites-activity-pill"
      title={`${data.total} ${camperWord} saved this campsite · ${data.recent_7d} in the last 7 days`}
    >
      <Heart className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#c8194c]" strokeWidth={2.2} />
      <span>
        {data.total} {camperWord} saved
        {when && data.recent_7d > 0 ? <> · last {when}</> : null}
      </span>
    </div>
  );
}
